import { useEffect, useRef, useState } from "react";
import { checkPhoneNumber } from "../API";
import "../styles/PhoneCheckControl.css";

const INVALID_PHONE_MESSAGE = "Введите корректный номер телефона";
const REQUEST_ERROR_MESSAGE = "Не удалось выполнить проверку. Попробуйте ещё раз.";
const EXCLUDED_SOURCES_PATTERN = /(^|\s)(циан|cian|яндекс|yandex)(\s|$)/i;

export function isConcreteComplexMatch(match) {
  const complex = String(match?.complex || "").trim();
  if (!complex) return false;
  const normalized = complex.toLocaleLowerCase("ru-RU").replace(/ё/g, "е").replace(/[^a-zа-я0-9]+/gi, " ").trim();
  return !EXCLUDED_SOURCES_PATTERN.test(normalized);
}

function PhoneCheckControl() {
  const rootRef = useRef(null);
  const requestIdRef = useRef(0);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const visibleMatches = Array.isArray(result?.matches) ? result.matches.filter(isConcreteComplexMatch) : [];

  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape" || (event.type === "mousedown" && !rootRef.current?.contains(event.target))) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", close);
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("mousedown", close);
      requestIdRef.current += 1;
    };
  }, []);

  const handleChange = (event) => {
    requestIdRef.current += 1;
    setPhone(event.target.value);
    setResult(null);
    setError("");
    setOpen(false);
    setLoading(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const value = phone.trim();
    if (!value || loading) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setResult(null);
    setError("");
    setOpen(false);

    try {
      const data = await checkPhoneNumber(value);
      if (requestIdRef.current !== requestId) return;
      setResult(data);
      setOpen(true);
    } catch (requestError) {
      if (requestIdRef.current !== requestId) return;
      setError(requestError?.status === 400 ? INVALID_PHONE_MESSAGE : REQUEST_ERROR_MESSAGE);
      setOpen(true);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  };

  return (
    <div className="phone-check-control" ref={rootRef}>
      <form className="phone-check-control__form" onSubmit={handleSubmit}>
        <label className="phone-check-control__label" htmlFor="phone-check-input">Проверить номер телефона</label>
        <input
          id="phone-check-input"
          className="phone-check-control__input"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder="Проверить номер"
          value={phone}
          onChange={handleChange}
          aria-controls="phone-check-result"
        />
        <button
          className="phone-check-control__button"
          type="submit"
          disabled={loading || !phone.trim()}
          aria-label={loading ? "Проверяем номер" : "Проверить номер"}
        >
          {loading ? <span className="phone-check-control__spinner" aria-hidden="true" /> : <span aria-hidden="true">⌕</span>}
        </button>
      </form>

      {open && (
        <div className="phone-check-control__dropdown" id="phone-check-result" role="status">
          {error ? (
            <p className="phone-check-control__message phone-check-control__message--error">{error}</p>
          ) : visibleMatches.length ? (
            <>
              <p className="phone-check-control__heading">Найдено:</p>
              <ul className="phone-check-control__matches">
                {visibleMatches.map((match, index) => (
                  <li className="phone-check-control__match" key={`${match.complex}-${match.developer}-${index}`}>
                    <strong>{match.complex}</strong>
                    {match.developer && <span>{match.developer}</span>}
                    {match.date && <span className="phone-check-control__date">Дата перевода: {match.date}</span>}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="phone-check-control__message">Совпадений за последние 3 месяца нет</p>
          )}
        </div>
      )}
    </div>
  );
}

export default PhoneCheckControl;
